// Send / Schedule Template Modal (WP-04).
import { useId, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { useTemplates, sendMessage } from '../../lib/whatsappApi';
import { scheduleMessage } from '../../lib/scheduled';
import type { LiveConversation } from '../../lib/inboxTypes';
import { CloseIcon, ClockIcon, SendIcon, SparklesIcon } from '../icons';

interface ScheduleTemplateModalProps {
  conv: LiveConversation;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ScheduleTemplateModal({
  conv,
  onClose,
  onSuccess,
}: ScheduleTemplateModalProps) {
  const { t } = useI18n();
  const { items: allTemplates, loading } = useTemplates();
  const approvedTemplates = useMemo(
    () => allTemplates.filter((tpl) => tpl.status === 'APPROVED'),
    [allTemplates],
  );

  const [selectedName, setSelectedName] = useState<string>('');
  const [variables, setVariables] = useState<Record<number, string>>({});
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<string>('');
  const [timezone, setTimezone] = useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Muscat';
    } catch {
      return 'Asia/Muscat';
    }
  });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const selectedTemplate = useMemo(
    () => approvedTemplates.find((tpl) => tpl.name === selectedName),
    [approvedTemplates, selectedName],
  );

  // Extract variable count from body component
  const bodyComponent = selectedTemplate?.components?.find((c) => c.type === 'BODY');
  const bodyText = bodyComponent?.text || '';
  const detectedVarIndices = useMemo(() => {
    const matches = bodyText.matchAll(/\{\{(\d+)\}\}/g);
    const indices: number[] = [];
    for (const match of matches) {
      const idx = parseInt(match[1], 10);
      if (!indices.includes(idx)) indices.push(idx);
    }
    return indices.sort((a, b) => a - b);
  }, [bodyText]);

  const handleSelectTemplate = (name: string) => {
    setSelectedName(name);
    setError(null);
    const tpl = approvedTemplates.find((x) => x.name === name);
    if (tpl) {
      // Auto-prefill {{1}} with contact name if applicable
      setVariables({ 1: conv.displayName || conv.name || '' });
    } else {
      setVariables({});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;

    // Check all variables are filled
    const varList = detectedVarIndices.map((idx) => variables[idx]?.trim() || '');
    if (detectedVarIndices.some((idx) => !variables[idx]?.trim())) {
      setError(t('tpl.fillAllVariables') || 'Please fill in all template variables');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (isScheduled) {
        if (!scheduledDateTime) {
          setError(t('inbox.schedule.timeRequired') || 'Please select a date and time to schedule');
          setSubmitting(false);
          return;
        }
        const sendAtEpoch = new Date(scheduledDateTime).getTime();
        if (isNaN(sendAtEpoch) || sendAtEpoch <= Date.now()) {
          setError(t('inbox.schedule.futureRequired') || 'Please select a future date and time');
          setSubmitting(false);
          return;
        }

        await scheduleMessage({
          conversationId: conv.id,
          payload: {
            templateName: selectedTemplate.name,
            templateLanguage: selectedTemplate.language || 'en',
            templateVariables: varList.length > 0 ? varList : undefined,
          },
          sendAt: scheduledDateTime,
          timezone,
        });
      } else {
        await sendMessage({
          to: conv.phone,
          conversationId: conv.id,
          templateName: selectedTemplate.name,
          templateLanguage: selectedTemplate.language || 'en',
          templateVariables: varList.length > 0 ? varList : undefined,
        });
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to send template');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-[85vh] max-h-[640px] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint">
              <SparklesIcon size={18} />
            </div>
            <div>
              <h3 id={titleId} className="text-sm font-bold text-ink dark:text-slate-100">
                {isScheduled
                  ? t('inbox.schedule.title') || 'Schedule WhatsApp Template'
                  : t('inbox.composer.sendTemplate') || 'Send WhatsApp Template'}
              </h3>
              <p className="text-xs text-ink-3 dark:text-slate-400">
                {conv.displayName || conv.name} ({conv.phone})
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {error && (
              <div className="rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
                {error}
              </div>
            )}

            {/* Template Selector */}
            <div>
              <label className="block text-xs font-bold text-ink mb-1.5 dark:text-slate-200">
                {t('inbox.composer.pickTemplate') || 'Select Approved Template'} <span className="text-rose-500">*</span>
              </label>
              {loading ? (
                <div className="text-xs text-ink-3 dark:text-slate-400">{t('common.loading') || 'Loading templates…'}</div>
              ) : (
                <select
                  value={selectedName}
                  onChange={(e) => handleSelectTemplate(e.target.value)}
                  required
                  className="w-full rounded-xl border border-line bg-surface px-3.5 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="">{t('inbox.composer.selectTemplateOption') || '— Choose a template —'}</option>
                  {approvedTemplates.map((tpl) => (
                    <option key={tpl.id || tpl.name} value={tpl.name}>
                      {tpl.name} ({tpl.language}) — {tpl.category}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Template Preview & Variables */}
            {selectedTemplate && (
              <div className="rounded-xl border border-line bg-slate-50 p-3.5 dark:border-slate-800 dark:bg-slate-850">
                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-4 dark:text-slate-500 mb-1.5">
                  {t('inbox.composer.templatePreview') || 'Template Preview'}
                </div>
                <p className="whitespace-pre-wrap text-xs text-ink-2 dark:text-slate-300 font-sans leading-relaxed">
                  {bodyText}
                </p>

                {detectedVarIndices.length > 0 && (
                  <div className="mt-3.5 border-t border-line pt-3 space-y-2.5 dark:border-slate-800">
                    <div className="text-xs font-bold text-ink dark:text-slate-200">
                      {t('inbox.composer.fillVariables') || 'Fill Variables'}
                    </div>
                    {detectedVarIndices.map((idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="shrink-0 w-12 font-mono text-xs font-bold text-slate-500">
                          {`{{${idx}}}`}
                        </span>
                        <input
                          type="text"
                          required
                          value={variables[idx] || ''}
                          onChange={(e) => setVariables({ ...variables, [idx]: e.target.value })}
                          placeholder={idx === 1 ? 'Customer Name' : `Variable {{${idx}}}`}
                          className="min-w-0 flex-1 rounded-lg border border-line bg-white px-3 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Schedule for later toggle */}
            <div className="border-t border-line pt-3 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClockIcon size={16} className="text-amber-500" />
                  <span className="text-xs font-bold text-ink dark:text-slate-200">
                    {t('inbox.schedule.toggleLabel') || 'Schedule for later delivery'}
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={isScheduled}
                  onChange={(e) => setIsScheduled(e.target.checked)}
                  className="h-4 w-4 rounded text-brand focus:ring-brand"
                />
              </div>

              {isScheduled && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border border-line bg-surface p-3 dark:border-slate-800 dark:bg-slate-800">
                  <div>
                    <label className="block text-[11px] font-bold text-ink mb-1 dark:text-slate-300">
                      {t('inbox.schedule.dateTime') || 'Date & Time'} <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      required={isScheduled}
                      value={scheduledDateTime}
                      onChange={(e) => setScheduledDateTime(e.target.value)}
                      className="w-full rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-ink mb-1 dark:text-slate-300">
                      {t('inbox.schedule.timezone') || 'Timezone'}
                    </label>
                    <input
                      type="text"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="w-full rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-line px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedTemplate}
              className={cn(
                'flex items-center gap-1.5 rounded-xl px-5 py-2 text-xs font-bold text-white shadow-sm transition disabled:opacity-50',
                isScheduled ? 'bg-amber-500 hover:bg-amber-600' : 'bg-brand hover:bg-brand-dark',
              )}
            >
              {isScheduled ? <ClockIcon size={14} /> : <SendIcon size={14} />}
              <span>
                {submitting
                  ? t('common.submitting') || 'Submitting…'
                  : isScheduled
                  ? t('inbox.schedule.confirm') || 'Schedule Message'
                  : t('inbox.composer.send') || 'Send Template'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
